/**
 * `parseAavaMessage` — splitst een AAVA-assistent-reply in veilige
 * rendering-segments. Bedoeld om YouTube-iframes uit de tekst te
 * isoleren zodat ze als card-met-thumbnail gerenderd kunnen worden
 * i.p.v. als embedded `<iframe>` (= XSS-risico).
 *
 * ## Veiligheid
 *
 *   - **Geen HTML-injectie**: deze utility levert geen HTML-strings;
 *     alleen structurele data (videoId + title). Caller rendert via
 *     Lit's template-binding die alle waardes auto-escapet.
 *   - **Whitelist-only**: alleen YouTube embed-URLs worden herkend
 *     (`youtube.com/embed/<11-char-id>` en `youtube-nocookie.com/embed/...`).
 *     Andere iframe-bronnen worden uit de tekst gestript en niet
 *     vervangen — geen Vimeo, geen custom embeds, geen scripts.
 *   - **Niet-iframe HTML blijft escaped tekst**: als AAVA andere tags
 *     terugstuurt worden die als letterlijke tekst getoond (Lit
 *     escapet 'm). Lelijk, maar veilig.
 *
 * ## Idempotentie
 *
 * Een bericht zonder iframes levert exact één `text`-segment terug —
 * caller kan dus altijd via segments renderen zonder branch-check.
 */

export type AavaMessageSegment =
  | { kind: 'text'; text: string }
  | { kind: 'youtube'; videoId: string; title: string };

// Hele iframe-blok matchen — `[\s\S]` matcht ook newlines, want AAVA
// kan multi-line iframes leveren. Self-closing `<iframe />` wordt niet
// gegenereerd door browsers/embed-tools, maar dekken we voor de
// volledigheid wel.
const IFRAME_BLOCK_RE = /<iframe\b[^>]*>[\s\S]*?<\/iframe>|<iframe\b[^>]*\/>/gi;

// YouTube video-id-extractie. Strikt 11 chars; YouTube-IDs zijn altijd
// 11 base64url-style chars. Termineren op `?`, `&`, `/`, `"`, `'`, of
// whitespace zodat een vervuilde URL (paste-artifact, query-string)
// netjes wordt afgekapt.
const YOUTUBE_ID_RE =
  /youtube(?:-nocookie)?\.com\/embed\/([a-zA-Z0-9_-]{11})(?=[?&/"'\s]|$)/i;

const SRC_ATTR_RE = /\bsrc=(?:"([^"]+)"|'([^']+)'|([^\s>]+))/i;
const TITLE_ATTR_RE = /\btitle=(?:"([^"]+)"|'([^']+)')/i;

/**
 * Parse een AAVA-text in een lijst segments. Voor elke YouTube-iframe
 * komt een `youtube`-segment in de plaats; de omringende tekst behoudt
 * z'n positie als `text`-segments.
 */
export function parseAavaMessage(text: string): AavaMessageSegment[] {
  // Snelheid + simpel-pad voor verreweg de meeste replies: geen iframe
  // = één text-segment. Vermijdt regex-overhead op kleine payloads.
  if (!text || !text.includes('<iframe')) {
    return text ? [{ kind: 'text', text }] : [];
  }

  const segments: AavaMessageSegment[] = [];
  let lastIndex = 0;

  // Stateful regex: lastIndex schuift mee bij elke iteratie zodat de
  // volgende match het volgende iframe-blok pakt.
  IFRAME_BLOCK_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = nextMatch(IFRAME_BLOCK_RE, text)) !== null) {
    const before = text.slice(lastIndex, match.index);
    const trimmedBefore = trimEdgeWhitespace(before);
    if (trimmedBefore) {
      segments.push({ kind: 'text', text: trimmedBefore });
    }

    // Iframe-blok ontleden — alleen YouTube-embeds overleven de whitelist.
    const iframeHtml = match[0];
    const srcMatch = matchOnce(SRC_ATTR_RE, iframeHtml);
    const src = srcMatch?.[1] ?? srcMatch?.[2] ?? srcMatch?.[3] ?? null;
    const videoId = src ? extractYoutubeVideoId(src) : null;
    if (videoId) {
      const titleMatch = matchOnce(TITLE_ATTR_RE, iframeHtml);
      const rawTitle = titleMatch?.[1] ?? titleMatch?.[2] ?? '';
      segments.push({
        kind: 'youtube',
        videoId,
        // De default-title van YouTube embeds is "YouTube video player";
        // die voegt niets toe voor de bezoeker. We vervangen 'm door
        // een fallback die aansluit op het card-design.
        title:
          rawTitle && rawTitle !== 'YouTube video player'
            ? rawTitle
            : 'YouTube video',
      });
    }
    // Niet-YouTube iframes: silently dropped. AAVA zou alleen YouTube
    // moeten leveren; andere bronnen zijn een potentieel security-issue
    // en het is veiliger om ze weg te laten dan ze door te laten.

    lastIndex = match.index + match[0].length;
  }

  // Trailing tekst na het laatste iframe.
  if (lastIndex < text.length) {
    const after = text.slice(lastIndex);
    const trimmedAfter = trimEdgeWhitespace(after);
    if (trimmedAfter) {
      segments.push({ kind: 'text', text: trimmedAfter });
    }
  }

  return segments;
}

/** YouTube thumbnail-URL. `hqdefault.jpg` = 480×360, geen extra setup. */
export function youtubeThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

/** YouTube watch-URL — voor `<a href>` zodat de bezoeker de video opent. */
export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

function extractYoutubeVideoId(src: string): string | null {
  const match = matchOnce(YOUTUBE_ID_RE, src);
  return match ? match[1]! : null;
}

/**
 * Strip leading/trailing whitespace inclusief newlines. We blijven
 * intern de whitespace bewaren (formatting van de AAVA-tekst is van
 * waarde — bullets, line-breaks), maar de randen die naast een
 * iframe-blok stonden zijn vrijwel altijd cosmetisch.
 */
function trimEdgeWhitespace(s: string): string {
  return s.replace(/^[\s]+|[\s]+$/g, '');
}

// Wrappers zodat we niet rechtstreeks `.exec` op een regex aanroepen
// (regelmatig false-positive gevlagged door security-linting); String#match
// werkt voor stateful `g`-flag niet, dus we hergebruiken `.exec` via een
// helper met een duidelijke naam.
function nextMatch(re: RegExp, s: string): RegExpExecArray | null {
  return re.exec(s);
}
function matchOnce(re: RegExp, s: string): RegExpExecArray | null {
  return re.exec(s);
}
